import React from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Summary line shown when the collapse is closed (and reused by RecordsPage /
 * App.js list rows as a compact preview of the original submission).
 *
 * Takes the first physical line, capped at ~250 chars (raised from the
 * original 96, T5.4) — long enough to cover a full first paragraph/sentence
 * of typical marketing copy without ballooning the summary itself into a
 * second wall of text. List-row callers additionally clip with CSS
 * (`truncate`/`max-w`), so this only changes what a screen reader announces
 * and what shows before the details panel is expanded.
 */
export function firstLine(value) {
  const text = String(value || '').trim();
  const line = text.split(/\r?\n/)[0] || text;
  const MAX = 250;
  return line.length > MAX ? `${line.slice(0, MAX)}...` : line;
}

/**
 * Collapsed-by-default view of the full original submitted text. Renders
 * nothing when there is no text (null-guarded, matches every other
 * data-gated section on the report). Unit/original text is user-pasted —
 * always rendered as plain text (never dangerouslySetInnerHTML).
 */
export default function OriginalInputCollapse({ originalText }) {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const text = String(originalText || '').trim();
  if (!text) {
    return null;
  }

  return (
    <section className="rd-section" aria-label={t('report.original.title')}>
      <details
        className="original-input-collapse"
        open={open}
        onToggle={(e) => setOpen(e.currentTarget.open)}
      >
        <summary>
          <span>{firstLine(text)}</span>
          <span className="expand-hint">
            {open ? t('report.units.showLess') : t('report.units.showFullText')} ▾
          </span>
        </summary>
        <pre>{text}</pre>
      </details>
    </section>
  );
}
